import {
  createApi,
  createSessionStore,
  getOrCreateDeviceId,
  type Api,
  type SessionStore,
} from "@kovernow/flows/api";
import { createAddItemApi, type AddItemApi } from "@kovernow/flows/add_item";
import { createAuthApi, type AuthApi, type IdentityProvider } from "@kovernow/flows/auth";
import { createFormatter, type Formatter } from "@kovernow/flows/format";
import { createHomeApi, type HomeApi } from "@kovernow/flows/home";
import {
  createKycApi,
  createShuftiApi,
  createSingpassApi,
  type KycApi,
  type ShuftiApi,
  type SingpassApi,
} from "@kovernow/flows/kyc";
import { createSupportApi, type SupportApi } from "@kovernow/flows/support";
import { createProfileApi, type ProfileApi } from "@kovernow/flows/profile";
import { createQuoteApi, type QuoteApi } from "@kovernow/flows/quote";
import { createVaultApi, type VaultApi } from "@kovernow/flows/vault";
import type { I18n } from "@kovernow/flows/i18n";
import { createHeicConverter, type HeicConverter, type StorageRemover, type UploadAdapter } from "@kovernow/flows/uploads";
import type { FirebaseUploadsOptions } from "@kovernow/flows/uploads/firebase";
import {
  createFreshness,
  createOnlineStatus,
  type Freshness,
  type OnlineStatus,
} from "@kovernow/flows/network";
import type { TenantWebConfig } from "@kovernow/types/tenant-web-config";
import { createSingpassReturnStore, type SingpassReturnStore } from "./singpass-return";

/** Everything a screen needs, created once at start-up (no globals). */
export interface AppServices {
  config: TenantWebConfig;
  i18n: I18n;
  api: Api;
  authApi: AuthApi;
  homeApi: HomeApi;
  profileApi: ProfileApi;
  vaultApi: VaultApi;
  /** W-10: server-priced plans and quotations. */
  quoteApi: QuoteApi;
  addItemApi: AddItemApi;
  /** SG only (W-7): the SingPass service; null without `singpassServiceUrl` (HK). */
  singpassApi: SingpassApi | null;
  kycApi: KycApi;
  /** HK only (W-8): server-created ShuftiPro verification + its latest result. */
  shuftiApi: ShuftiApi;
  /** Region-aware support cases ("Need help verifying?"). */
  supportApi: SupportApi;
  /** The SingPass return captured off the URL at load (memory only). */
  singpassReturn: SingpassReturnStore;
  /** Full-page navigation away (SingPass, ShuftiPro); replaceable in tests. */
  leavePage: (url: string) => void;
  /** Storage uploads: a separate, in-memory Firebase app. */
  uploads: Uploads;
  convertHeic: HeicConverter;
  session: SessionStore;
  identity: IdentityProvider;
  format: Formatter;
  online: OnlineStatus;
  freshness: Freshness;
}

export interface Uploads extends UploadAdapter, StorageRemover {
  /** Sign the uploads app out (sign-out and session loss). */
  signOut(): Promise<void>;
}

/** E2E only (Storage emulator): a dead connection fails fast. */
const E2E_MAX_UPLOAD_RETRY_MS = 3_000;

/**
 * The Firebase Storage adapter, loaded on the first upload so Firebase
 * Storage stays out of the start-up bundle.
 */
function lazyFirebaseUploads(opts: FirebaseUploadsOptions): Uploads {
  let loading: Promise<Uploads> | null = null;
  const load = () =>
    (loading ??= import("@kovernow/flows/uploads/firebase").then(
      (m) => m.createFirebaseUploads(opts),
      (err: unknown) => {
        // A failed chunk load (network blip, new deploy) must not stick until reload.
        loading = null;
        throw err;
      },
    ));
  return {
    upload: async (blob, req) => (await load()).upload(blob, req),
    remove: async (ownerId, location) => (await load()).remove(ownerId, location),
    async signOut() {
      if (loading) await (await loading).signOut();
    },
  };
}

export function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export interface CreateServicesOptions {
  config: TenantWebConfig;
  i18n: I18n;
  identity: IdentityProvider;
  fetch?: typeof fetch;
  onSessionExpired?: () => void;
  /** Created (and filled from the URL) before anything else at page load. */
  singpassReturn?: SingpassReturnStore;
}

export function createServices(opts: CreateServicesOptions): AppServices {
  const session = createSessionStore();
  const freshness = createFreshness();
  const api = createApi({
    region: opts.config.region,
    apiBaseUrl: opts.config.apiBaseUrl,
    session,
    deviceId: getOrCreateDeviceId(safeLocalStorage()),
    fetch: opts.fetch,
    onSessionExpired: opts.onSessionExpired,
    onSuccess: () => freshness.markFresh(),
  });
  const addItemApi = createAddItemApi(api);
  return {
    config: opts.config,
    i18n: opts.i18n,
    api,
    authApi: createAuthApi(api),
    homeApi: createHomeApi(api),
    profileApi: createProfileApi(api),
    vaultApi: createVaultApi(api),
    quoteApi: createQuoteApi(api),
    addItemApi,
    singpassApi: opts.config.singpassServiceUrl
      ? createSingpassApi(opts.config.singpassServiceUrl, opts.fetch)
      : null,
    kycApi: createKycApi(api),
    shuftiApi: createShuftiApi(api),
    supportApi: createSupportApi(api),
    singpassReturn: opts.singpassReturn ?? createSingpassReturnStore(),
    leavePage: (url) => window.location.assign(url),
    uploads: lazyFirebaseUploads({
      config: opts.config.firebase,
      mintToken: () => addItemApi.uploadToken(),
      maxUploadRetryTime: opts.config.firebase.storageEmulatorHost ? E2E_MAX_UPLOAD_RETRY_MS : undefined,
    }),
    convertHeic: createHeicConverter(),
    session,
    identity: opts.identity,
    format: createFormatter({
      locale: opts.config.locale,
      currency: opts.config.currency,
      timeZone: opts.config.timeZone,
    }),
    online: createOnlineStatus(window),
    freshness,
  };
}
